import Swiper from 'swiper';
import { Navigation, Thumbs } from 'swiper/modules';
import FancyboxModule from './shared/utils/fancybox-module.js';
import { getNodeIndex } from './helpers/get-node-index';

class productMediaSlider extends HTMLElement {
  constructor() {
    super();
    this.reducer = {
      slideToVariant: (e) => this.slideToVariant(e),
    };
  }

  connectedCallback() {
    this.abortController = new AbortController();

    // Create elements
    this.thumbSlider();
    this.mainSlider();
    this.fancybox();

    // Listen for variant change and go to slide
    document.addEventListener('variant-change', this.reducer.slideToVariant, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /*
   * Create thumb slider
   */
  thumbSlider() {
    // Helper function to get direction
    const getDirection = () => {
      const direction = window.innerWidth <= 480 ? 'horizontal' : 'vertical';

      return direction;
    };

    // Get Thumbnails slider element & Set swiper options
    this.swiperThumbs = this.querySelector('[data-product-media-thumbnails-slider]');
    this.swiperThumbsOptions = {
      slidesPerView: 3.8,
      spaceBetween: 8,
      direction: 'vertical',
      slidesOffsetAfter: 5,
      watchSlidesProgress: true,
      modules: [Navigation],
      navigation: {
        nextEl: '.swiper-button-thumbnails-next',
        prevEl: '.swiper-button-thumbnails-prev',
      },
      breakpoints: {
        480: {
          slidesPerView: 4,
        },
      },
      on: {
        resize(swiper) {
          swiper.changeDirection(getDirection());
        },
      },
    };

    // Create Thumbnails slider
    this.swiperThumbsInstance = new Swiper(this.swiperThumbs, this.swiperThumbsOptions);
  }

  /*
   * Create main slider
   */
  mainSlider() {
    // Get swiper
    this.swiperMain = this.querySelector('[data-product-media-slider]');

    // Get thumb options
    let thumbOptions;

    if (this.swiperThumbsInstance) {
      thumbOptions = {
        thumbs: {
          swiper: this.swiperThumbsInstance,
        },
      };
    }

    // Get initial slide and swiper index
    let initialSlideIndex = 0;
    const initalVariant = parseInt(this.swiperMain.dataset.productMediaSlider, 10);

    if (initalVariant) {
      const initialSlide = this.matchVariantIdWithSlide(initalVariant);

      initialSlideIndex = initialSlide || initialSlideIndex;
    }

    // Thumbnails Slider Ref for sliding current thumb always to first.
    // Cannot use this.swiperThumbsInstanceRef due to scoping in event handler.
    const swiperThumbsInstanceRef = this.swiperThumbsInstance;

    // Swiper options
    this.swiperMainOptions = {
      direction: 'horizontal',
      initialSlide: initialSlideIndex,
      modules: [Navigation, Thumbs],
      navigation: {
        nextEl: '.swiper-button-main-next',
        prevEl: '.swiper-button-main-prev',
      },
      ...thumbOptions,
      on: {
        activeIndexChange(swiper) {
          // swiper.thumbs does not have slideTo method, only update and this is not triggering transition.
          swiperThumbsInstanceRef.slideTo(swiper.activeIndex);
        },
      },
    };

    // Create Main slider
    this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);
  }

  /*
   * Create fancybox
   */
  fancybox() {
    FancyboxModule();
  }

  /*
   * Slide to variant
   * @param e {object}: event object with variant
   */
  slideToVariant(e) {
    if (e?.detail?.variant?.id) {
      const slideIndex = this.matchVariantIdWithSlide(e.detail.variant.id);

      if (typeof slideIndex === 'number') {
        this.goToSlide(slideIndex);
      }
    }
  }

  /*
   * Match variant id with slide
   * @param id {int}: variant id
   */
  matchVariantIdWithSlide(id) {
    if (!id || !this.swiperMain) {
      return false;
    }

    // Get slides
    const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

    // Match id with slides id's
    const slideMatch = [...slides].find((slide) => {
      // Get variant id's attached to slide
      const slideVariants = JSON.parse(slide.dataset.productVariantImage);

      // Check if variant id match the slide variant id's
      return slideVariants.includes(id) ? slide : false;
    });

    if (!slideMatch) {
      return false;
    }

    // return matched slide index
    return getNodeIndex(slideMatch);
  }

  /*
   * go to slide
   * @param slide {node} / {int}: slide node / index
   */
  goToSlide(slide) {
    if ((typeof slide !== 'number' && typeof slide !== 'object') || !this.swiperMainInstance) {
      return false;
    }

    // Get index, transform node to slide index or string to int
    const index = typeof slide === 'number' ? parseInt(slide, 10) : getNodeIndex(slide);

    // go to slide
    this.swiperMainInstance.slideTo(index);
  }
}

// Load product-media-swiper if that exists
if (!customElements.get('product-media-slider')) {
  customElements.define('product-media-slider', productMediaSlider);
}
